/**
 * Phase 11 — where the Hour Optimizer's extra drops land, pure and shared:
 * the planner runs it to build the day (and adds stable ids), the editor runs
 * it on the DRAFT settings to draw the ⚡ marks as you edit. One extra drop in
 * each of the best-ranked hours (up to maxExtraSlots) and one probe in the
 * busiest UNRANKED hour; every drop keeps minIntervalMin from every
 * neighbour, as close to mid-hour as that allows; hours outside the span, or
 * without room, are skipped. Only minutes inside the country's window count
 * (it is a hard limit — a peak hour outside it gets nothing).
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenRule, DropGenRuleScope } from "../rules/rules.types";
import { inCountryWindow, type CountryWindow } from "../window/country-window";

export interface HourExtrasInput {
  startMin: number;
  /** Inclusive — the container end, or its last regular slot when none. */
  endMin: number;
  regularMinutes: number[];
  minIntervalMin: number;
  maxExtraSlots: number;
  /** Ranked hours, best first. */
  ranked: number[];
  /** Unranked hours, busiest first. */
  unranked: number[];
  /** The country's window; absent/null = no limit (a master config's preview). */
  window?: CountryWindow | null;
}

export interface HourExtra {
  minute: number;
  kind: "extra" | "probe";
  hour: number;
  /** The hour's rank for an extra; null for the probe. */
  rank: number | null;
}

type TryBucket = {
  hour: number;
  rank: number | null;
  unranked: boolean;
  uniqueVisits: number;
  /** Phase 19 — the hour rules' word on it. */
  ruleHits?: ReadonlyArray<{ action: string; state: string; name?: string; reason?: string; ruleId?: string }>;
};

const hit = (b: TryBucket, action: string) =>
  (b.ruleHits ?? []).some((h) => h.state === "fired" && h.action === action);

/** The order the planner tries hours in: ranked best-first, unranked
 *  busiest-first. Phase 19: an hour a "never boost" or "block" rule fired on
 *  gets no extra and no probe; a "boost first" hour goes to the front. */
export function hourTryOrder(buckets: ReadonlyArray<TryBucket>): { ranked: number[]; unranked: number[] } {
  const open = buckets.filter((b) => !hit(b, "no_extra") && !hit(b, "block"));
  const first = open.filter((b) => hit(b, "extra_first"));
  const ranked = open
    .filter((b) => !b.unranked && b.rank !== null && !first.includes(b))
    .sort((a, b) => (a.rank as number) - (b.rank as number));
  const unranked = open
    .filter((b) => b.unranked && !first.includes(b))
    .sort((a, b) => b.uniqueVisits - a.uniqueVisits || a.hour - b.hour);
  const firstSorted = [...first].sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99) || b.uniqueVisits - a.uniqueVisits);
  return { ranked: [...firstSorted, ...ranked].map((b) => b.hour), unranked: unranked.map((b) => b.hour) };
}

export interface BlockedHour {
  hour: number;
  name: string;
  reason: string;
  /** Phase 26 — which groups' drops the block holds (absent = every group). */
  scope?: DropGenRuleScope;
}

/** Phase 19 — hours a "block" rule fired on, with the rule's reason (and, Phase 26, its list scope — the FIRST
 *  fired block rule of the hour decides; a scoped one never widens an unscoped one). */
export function blockedHours(buckets: ReadonlyArray<TryBucket>, rules?: ReadonlyArray<Pick<DropGenRule, "id" | "scope">>): BlockedHour[] {
  const scopeOf = new Map((rules ?? []).map((r) => [r.id, r.scope]));
  return buckets.flatMap((b) => {
    const hits = (b.ruleHits ?? []).filter((x) => x.state === "fired" && x.action === "block");
    if (hits.length === 0) return [];
    const h = hits.find((x) => !x.ruleId || !scopeOf.get(x.ruleId)) ?? hits[0];
    const scope = h.ruleId ? scopeOf.get(h.ruleId) : undefined;
    return [{ hour: b.hour, name: h.name ?? "a rule", reason: h.reason ?? "", ...(scope ? { scope } : {}) }];
  });
}

/** Where an hour's drop lands inside [startMin, endMin] and the window: the
 *  minute of that hour with at least `floor` minutes of room from every taken
 *  minute, as close to mid-hour as possible (earlier on ties); null when the
 *  hour lies outside the span or the window, or has no such room. The span may
 *  cross midnight (minutes past 1439 are the next date). */
export function placeInHour(
  hour: number,
  span: { startMin: number; endMin: number; window?: CountryWindow | null },
  taken: number[],
  floor: number,
): number | null {
  for (let base = 0; base <= span.endMin; base += 1440) {
    const hourStart = base + hour * 60;
    const lo = Math.max(hourStart, span.startMin);
    const hi = Math.min(hourStart + 59, span.endMin);
    if (lo > hi) continue;
    const mid = hourStart + 30;
    let best: number | null = null;
    for (let m = lo; m <= hi; m += 1) {
      if (span.window && !inCountryWindow(m, span.window)) continue;
      const room = taken.reduce(
        (r, t) => Math.min(r, Math.abs(t - m)),
        Infinity,
      );
      if (room < floor) continue;
      if (best === null || Math.abs(m - mid) < Math.abs(best - mid)) best = m;
    }
    return best;
  }
  return null;
}

export function planHourExtras(i: HourExtrasInput): HourExtra[] {
  if (i.endMin < i.startMin) return [];
  const taken = [...i.regularMinutes];
  const out: HourExtra[] = [];
  const place = (
    hour: number,
    kind: HourExtra["kind"],
    rank: number | null,
  ) => {
    const minute = placeInHour(hour, i, taken, i.minIntervalMin);
    if (minute === null) return false;
    taken.push(minute);
    out.push({ minute, kind, hour, rank });
    return true;
  };
  let extras = 0;
  for (const [n, hour] of i.ranked.entries()) {
    if (extras >= i.maxExtraSlots) break;
    if (place(hour, "extra", n + 1)) extras += 1;
  }
  for (const hour of i.unranked) {
    if (place(hour, "probe", null)) break;
  }
  return out.sort((a, b) => a.minute - b.minute);
}
